import { Injectable, inject } from '@angular/core';
import { ActivityEntityName, EntityStatusType, PickerEntityStatusRequest, PortalEntity, StatusPickerOption, TaskSystemStatusName, tryCatch } from '@jms/model';
import { useState } from '@jms/shared/ngrx-actions';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { firstValueFrom } from 'rxjs';

// The Tasks set, read by both the Task detail surface and the SOR detail page rather than hardcoded, since a tenant may rename or add statuses.
@Injectable({ providedIn: 'root' })
export class TaskStatusSetService {

  private readonly entityData = inject(EntityDataServiceRaw);
  // A portal user switches tenant without a reload (WBS-12033); the office has no selected entity, so its key never moves.
  private readonly $selectedEntity = useState<PortalEntity | null>('application.selectedEntity');

  private tenant: string | null = null;
  private closing: Promise<StatusPickerOption[]> | null = null;
  private statuses: Promise<StatusPickerOption[]> | null = null;
  private all: Promise<StatusPickerOption[] | null> | null = null;

  /** Inactive statuses a user may choose, minus Completed, which is the page's own one-click button. */
  public loadClosingStatuses(): Promise<StatusPickerOption[]> {

    this.followTenant();
    this.closing ??= this.fetch({ entityType: ActivityEntityName.Task, displayType: EntityStatusType.Inactive, restrictToSelectable: true }, () => this.closing = null)
      .then(items => (items ?? []).filter(item => item.systemName !== TaskSystemStatusName.Completed));

    return this.closing;
  }

  /** The whole selectable Tasks set, Completed included: Change status excludes it by id, Complete reads its reasonRequired. */
  public loadStatuses(): Promise<StatusPickerOption[]> {

    this.followTenant();
    this.statuses ??= this.fetch({ entityType: ActivityEntityName.Task, restrictToSelectable: true }, () => this.statuses = null)
      .then(items => items ?? []);

    return this.statuses;
  }

  /** Every active Tasks status whoever may pick it, or null when the read failed: a gate on `editableBy` must not read a failure as an empty set. */
  public loadAllStatuses(): Promise<StatusPickerOption[] | null> {

    this.followTenant();
    this.all ??= this.fetch({ entityType: ActivityEntityName.Task }, () => this.all = null);

    return this.all;
  }

  private followTenant(): void {

    const tenant = this.$selectedEntity()?.tenantName ?? '';
    if (tenant === this.tenant) return;

    this.tenant = tenant;
    this.closing = null;
    this.statuses = null;
    this.all = null;
  }

  private async fetch(request: PickerEntityStatusRequest, onError: () => void): Promise<StatusPickerOption[] | null> {

    const [error, response] = await tryCatch(firstValueFrom(this.entityData.getPicker(ActivityEntityName.EntityStatus, request)));

    // A failed read must not cache: the next open of the menu should try again.
    if (error) {
      onError();
      return null;
    }

    // Boundary cast: the picker response is untyped JSON; StatusPickerOption is the shape this picker always returns.
    return (response?.items as StatusPickerOption[]) ?? [];
  }
}
